import { TooltipDirective } from '../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../componentes/botones';
import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import * as XLSX from 'xlsx-js-style';
import {
  IEstadisticasPorCliente,
  IEstadisticasPorUsuario,
  IEstadisticasResumen,
  IEstadisticasSeriePunto,
} from 'modelos/src';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { fechaCelda, haceCelda } from '../../../../auxiliares/tabla/celdas';
import {
  EstadisticasUsoService,
  IEstadisticasFiltro,
} from '../../../../auxiliares/servicios/http/estadisticas-uso.service';
import {
  DatePickerComponent,
  DateRangeValue,
} from '../../../../componentes/date-picker/date-picker.component';
import { ChartCardComponent } from '../../../../componentes/chart-card/chart-card.component';
import { DashboardFiltrosComponent } from '../../../../componentes/dashboard-filtros/dashboard-filtros.component';
import { DashboardPanelComponent } from '../../../../componentes/dashboard-panel/dashboard-panel.component';
import { StatCardComponent } from '../../../../componentes/stat-card/stat-card.component';
import { ClienteSelectorComponent } from '../../../../componentes/selectores/cliente-selector/cliente-selector.component';

@Component({
  selector: 'app-estadisticas-uso',
  standalone: true,
  imports: [TooltipDirective, 
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    BOTONES,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    
    DatePickerComponent,
    ChartCardComponent,
    DashboardFiltrosComponent,
    DashboardPanelComponent,
    StatCardComponent,
    ClienteSelectorComponent,
  ],
  templateUrl: './estadisticas-uso.component.html',
  styleUrls: ['./estadisticas-uso.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EstadisticasUsoComponent implements OnInit {
  // Fechas con el formato común de los listados (24 h, huso del navegador)
  public fechaCelda = fechaCelda;
  public haceCelda = haceCelda;

  private service = inject(EstadisticasUsoService);
  public helper = inject(HelperService);

  // Filtro de rango
  public rango: DateRangeValue = { start: '', end: '' };
  public hoy = new Date();

  // Datos
  public resumen?: IEstadisticasResumen;
  public serie: IEstadisticasSeriePunto[] = [];
  public porCliente: IEstadisticasPorCliente[] = [];
  public usuarios: IEstadisticasPorUsuario[] = [];
  public totalUsuarios = 0;

  // Estado de carga
  public cargandoResumen = false;
  public cargandoSerie = false;
  public cargandoClientes = false;
  public cargandoUsuarios = false;
  public exportando = false;

  // Paginación (server-side) de la tabla por usuario
  public page = 0;
  public limit = 25;
  public sort = '-ultimoLogin';

  // Filtros propios de la tabla por usuario (no afectan resumen/serie/por-cliente)
  public filtroCliente: string | null = null;
  public busqueda = '';
  private busquedaTimer?: ReturnType<typeof setTimeout>;

  // Tarjetas de indicador: título, ícono y campo del resumen que muestran
  public readonly tarjetas: {
    titulo: string;
    icono: string;
    campo: keyof IEstadisticasResumen;
  }[] = [
    { titulo: 'Activos hoy (DAU)', icono: 'today', campo: 'dau' },
    { titulo: 'Activos 7 días (WAU)', icono: 'date_range', campo: 'wau' },
    { titulo: 'Activos 30 días (MAU)', icono: 'calendar_month', campo: 'mau' },
    { titulo: 'Activos en período', icono: 'group', campo: 'usuariosActivos' },
    {
      titulo: 'Usuarios registrados',
      icono: 'people_alt',
      campo: 'usuariosRegistrados',
    },
    { titulo: 'Logins en período', icono: 'login', campo: 'totalLogins' },
  ];

  // Columnas
  public columnasClientes = ['cliente', 'nivel', 'logins', 'activos'];
  public columnasUsuarios = [
    'usuario',
    'cliente',
    'ultimoLogin',
    'diasDesde',
    'loginsTotales',
    'loginsRango',
    'diasActivos',
    'racha',
    'plataformas',
  ];

  // Opciones del gráfico (Highcharts.Options)
  public chartOptions: any = {};

  ngOnInit(): void {
    // Rango por defecto: últimos 30 días.
    const desde = new Date();
    desde.setDate(desde.getDate() - 30);
    desde.setHours(0, 0, 0, 0);
    this.rango = { start: desde.toISOString(), end: new Date().toISOString() };
    this.recargar();
  }

  private get filtroBase(): IEstadisticasFiltro {
    return { desde: this.rango?.start, hasta: this.rango?.end };
  }

  public onRangoChange(): void {
    this.page = 0;
    this.recargar();
  }

  public recargar(): void {
    this.cargarResumen();
    this.cargarSerie();
    this.cargarClientes();
    this.cargarUsuarios();
  }

  private async cargarResumen(): Promise<void> {
    this.cargandoResumen = true;
    try {
      this.resumen = await this.service.resumen(this.filtroBase);
    } finally {
      this.cargandoResumen = false;
    }
  }

  private async cargarSerie(): Promise<void> {
    this.cargandoSerie = true;
    try {
      this.serie = (await this.service.serie(this.filtroBase)) || [];
      this.armarGrafico();
    } finally {
      this.cargandoSerie = false;
    }
  }

  private async cargarClientes(): Promise<void> {
    this.cargandoClientes = true;
    try {
      this.porCliente = (await this.service.porCliente(this.filtroBase)) || [];
    } finally {
      this.cargandoClientes = false;
    }
  }

  private get filtroUsuarios(): IEstadisticasFiltro {
    return {
      ...this.filtroBase,
      idCliente: this.filtroCliente || undefined,
      busqueda: this.busqueda?.trim() || undefined,
    };
  }

  private async cargarUsuarios(): Promise<void> {
    this.cargandoUsuarios = true;
    try {
      const res = await this.service.porUsuario({
        ...this.filtroUsuarios,
        page: this.page,
        limit: this.limit,
        sort: this.sort,
      });
      this.usuarios = res?.datos || [];
      this.totalUsuarios = res?.totalCount || 0;
    } finally {
      this.cargandoUsuarios = false;
    }
  }

  public onPage(e: PageEvent): void {
    this.page = e.pageIndex;
    this.limit = e.pageSize;
    this.cargarUsuarios();
  }

  // El filtro por cliente / búsqueda solo recarga la tabla por usuario.
  public onFiltroClienteChange(idCliente: string | null): void {
    this.filtroCliente = idCliente;
    this.page = 0;
    this.cargarUsuarios();
  }

  public onBusquedaChange(): void {
    clearTimeout(this.busquedaTimer);
    this.busquedaTimer = setTimeout(() => {
      this.page = 0;
      this.cargarUsuarios();
    }, 400);
  }

  private armarGrafico(): void {
    // La serie viene con días ISO 'yyyy-MM-dd'; se muestran como dd/MM/yyyy.
    const categorias = this.serie.map((p) => {
      const [anio, mes, dia] = (p.fecha || '').slice(0, 10).split('-');
      return anio && mes && dia ? `${dia}/${mes}/${anio}` : p.fecha || '';
    });
    const logins = this.serie.map((p) => p.logins || 0);
    const activos = this.serie.map((p) => p.usuariosActivos || 0);
    this.chartOptions = {
      chart: { type: 'areaspline' },
      title: { text: '' },
      xAxis: {
        categories: categorias,
        tickInterval: Math.ceil(Math.max(categorias.length / 12, 1)),
      },
      yAxis: { title: { text: '' }, allowDecimals: false, min: 0 },
      legend: { enabled: true },
      tooltip: { shared: true },
      plotOptions: {
        areaspline: { fillOpacity: 0.25, marker: { enabled: false } },
      },
      series: [
        { name: 'Logins', type: 'areaspline', data: logins },
        { name: 'Usuarios activos', type: 'line', data: activos },
      ],
    };
  }

  public async exportar(): Promise<void> {
    this.exportando = true;
    try {
      // Traigo el set completo (no solo la página actual) para el export.
      const res = await this.service.porUsuario({
        ...this.filtroUsuarios,
        page: 0,
        limit: 5000,
        sort: this.sort,
      });
      const filas: (string | number)[][] = [
        [
          'Usuario',
          'Nombre',
          'Cliente',
          'Último login',
          'Días desde último',
          'Logins totales',
          'Logins en rango',
          'Días activos',
          'Racha (días)',
          'Plataformas',
        ],
      ];
      for (const u of res?.datos || []) {
        filas.push([
          u.usuario || '',
          u.nombre || '',
          u.clienteNombre || '',
          u.ultimoLogin ? HelperService.parseDate(new Date(u.ultimoLogin)) : '',
          u.diasDesdeUltimoLogin ?? '',
          u.cantidadLogins ?? 0,
          u.loginsRango ?? 0,
          u.diasActivosRango ?? 0,
          u.rachaActual ?? 0,
          (u.plataformas || []).join(', '),
        ]);
      }
      const hoja = XLSX.utils.aoa_to_sheet(filas);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, hoja, 'Uso por usuario');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `estadisticas-uso-${stamp}.xlsx`);
    } finally {
      this.exportando = false;
    }
  }
}
